"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconLoadingCircle from "@ui/central-icons/icons/IconLoadingCircle";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import { RandomFact } from "@ui/components/core/RandomFact";
import { BeforeAfterDemo } from "@/app/_blocks/BlockBentoGrid/BeforeAfterDemo";
import { VideoPlayer } from "@ui/components/demos/ImageVideoHero/VideoPlayer";
import type { ContentWorkflowStep, WorkflowResultType } from "./types";
import { TransparencyCheckerboard } from "./TransparencyCheckerboard";

export interface StaticHeroMediaProps {
  type: "before_after" | "before_after_video" | "video" | "image" | "empty";
  beforeImageUrl?: string | null;
  afterImageUrl?: string | null;
  afterVideoUrl?: string | null;
  videoUrl?: string | null;
  imageUrl?: string | null;
  imageAlt?: string;
}

interface DynamicHeroMediaProps {
  step: ContentWorkflowStep;
  resultUrl: string | null;
  inputPreviewUrl: string | null;
  inputIsVideo: boolean;
  resultType: WorkflowResultType;
  staticMedia: StaticHeroMediaProps;
  onDownloadClick?: () => void;
}

export function DynamicHeroMedia({
  step,
  resultUrl,
  inputPreviewUrl,
  inputIsVideo,
  resultType,
  staticMedia,
  onDownloadClick,
}: DynamicHeroMediaProps) {
  const tHero = useTranslations("ClientKeys.creative_templates");
  const tGlobals = useTranslations("ClientKeys.Globals");

  // Result state: show result media
  if ((step === "result" || step === "quota_exceeded") && resultUrl) {
    // For video results with an input preview: show before/after
    if (resultType === "video" && inputPreviewUrl) {
      return (
        <BeforeAfterDemo
          {...(inputIsVideo ? { beforeVideo: inputPreviewUrl } : { beforeImage: inputPreviewUrl })}
          afterVideo={resultUrl}
          beforeLabel={tGlobals("input")}
          afterLabel={tGlobals("output")}
        />
      );
    }

    // For video results without input image: show video player
    if (resultType === "video") {
      return <VideoPlayer src={resultUrl} autoPlay loop muted hasAudio rounded={false} />;
    }

    // Show result image full-frame with download button
    return (
      <>
        <TransparencyCheckerboard />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={resultUrl}
          alt='Generated result'
          className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover'
        />
        <DownloadOverlayButton onClick={onDownloadClick} />
      </>
    );
  }

  // Processing state: show "Running workflow..." overlay while the generate API
  // is running. Not shown during "uploading" since a rate-limit rejection can
  // still occur. Legacy file-upload templates pass `inputPreviewUrl` so the
  // user's input stays visible behind the overlay; flow templates without a
  // single canonical input image fall back to the striped loader.
  if (step === "generating") {
    return (
      <InputPreviewWithOverlay
        inputPreviewUrl={inputPreviewUrl}
        inputIsVideo={inputIsVideo}
        fallback={<StripedLoader />}
      >
        <WorkflowPillWithFact label={tHero("running_workflow")} />
      </InputPreviewWithOverlay>
    );
  }

  // Idle / rate-limited / default: show static CMS media. The form card
  // carries the rate-limit description + sign-up CTA on its own.
  return <StaticHeroMedia {...staticMedia} />;
}

function InputPreviewWithOverlay({
  inputPreviewUrl,
  inputIsVideo,
  fallback,
  children,
}: {
  inputPreviewUrl: string | null;
  inputIsVideo: boolean;
  fallback: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <>
      {inputPreviewUrl ? (
        inputIsVideo ? (
          <video
            src={inputPreviewUrl}
            className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover'
            muted
            playsInline
          />
        ) : (
          // Blob URL from URL.createObjectURL — next/image requires known dimensions and a configured remote hostname
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={inputPreviewUrl}
            alt='Input preview'
            className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover'
          />
        )
      ) : (
        fallback
      )}
      <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/20 tw-backdrop-blur-[2px]'>
        {children}
      </div>
    </>
  );
}

function WorkflowPillWithFact({ label }: { label: string }) {
  return (
    <div className='tw-flex tw-w-[260px] tw-flex-col tw-items-center tw-rounded-2xl tw-bg-white tw-px-5 tw-py-6 tw-shadow-lg'>
      <div className='tw-flex tw-items-center tw-gap-2.5'>
        <IconLoadingCircle className={`${iconStrokeFix} tw-size-5 tw-shrink-0 tw-animate-spin`} />
        <span className='tw-type-sm tw-font-medium tw-text-dark tw-whitespace-nowrap'>{label}</span>
      </div>
      <RandomFact />
    </div>
  );
}

const STRIPED_BG_IMAGE =
  "repeating-linear-gradient(-45deg, rgba(255,255,255,0.34), rgba(255,255,255,0.34) 8px, rgba(255,255,255,0) 8px, rgba(255,255,255,0) 16px)";

function StripedLoader() {
  return (
    <div
      aria-hidden='true'
      className='tw-pointer-events-none tw-absolute tw-inset-0 tw-animate-pulse tw-overflow-hidden tw-bg-gray-200'
    >
      <div
        className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-animate-[move-bg_30s_linear_infinite]'
        style={{ backgroundImage: STRIPED_BG_IMAGE }}
      />
    </div>
  );
}

function StaticHeroMedia({
  type,
  beforeImageUrl,
  afterImageUrl,
  afterVideoUrl,
  videoUrl,
  imageUrl,
  imageAlt,
}: StaticHeroMediaProps) {
  const t = useTranslations("ClientKeys.Globals");

  if (type === "before_after_video" && beforeImageUrl && afterVideoUrl) {
    return (
      <BeforeAfterDemo
        beforeImage={beforeImageUrl}
        afterVideo={afterVideoUrl}
        beforeLabel={t("input")}
        afterLabel={t("output")}
      />
    );
  }

  if (type === "before_after" && beforeImageUrl && afterImageUrl) {
    return (
      <BeforeAfterDemo
        beforeImage={beforeImageUrl}
        afterImage={afterImageUrl}
        beforeLabel={t("input")}
        afterLabel={t("output")}
      />
    );
  }

  if (type === "video" && videoUrl) {
    return <VideoPlayer src={videoUrl} autoPlay loop muted hasAudio rounded={false} />;
  }

  if (type === "image" && imageUrl) {
    return (
      <Image
        src={imageUrl}
        alt={imageAlt || ""}
        fill
        className='tw-object-cover'
        sizes='(max-width: 1024px) 100vw, 50vw'
      />
    );
  }

  return <div className='tw-h-full tw-w-full tw-bg-dark' />;
}

function DownloadOverlayButton({ onClick }: { onClick?: () => void }) {
  const tGlobals = useTranslations("ClientKeys.Globals");
  const downloadLabel = tGlobals("download");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            onClick={onClick}
            className='tw-absolute tw-bottom-4 tw-right-4 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/50 tw-text-white tw-opacity-70 tw-outline-none tw-transition-opacity hover:tw-opacity-100 focus-visible:tw-outline-focus'
            aria-label={downloadLabel}
          >
            <IconArrowInbox className={`${iconStrokeFix} tw-size-5`} />
          </button>
        </TooltipTrigger>
        <TooltipContent variant='dark-new'>{downloadLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
